import React, { createContext, useContext, useState, useEffect } from 'react';
import { CHAIN_CONFIG, SUPPORTED_CHAINS, getChainNetwork, getChainFeatures } from '../constants/chains';
import { getProvider, clearProviderCache } from '../utils/chainUtils';
import { checkNetworkHealth, reconnectProvider, validateChainConfig, handleChainError } from '../utils/coreUtils';

// Create custom event for network changes
export const NETWORK_CHANGE_EVENT = 'network_change';

const ChainContext = createContext();

export const useChain = () => {
  const context = useContext(ChainContext);
  if (!context) {
    throw new Error('useChain must be used within a ChainProvider');
  }
  return context;
};

export const ChainProvider = ({ children }) => {
  // Initialize testnet state from localStorage or default to true
  const getSavedNetworkMode = () => {
    try {
      const savedMode = localStorage.getItem('isTestnet');
      return savedMode !== null ? savedMode === 'true' : false; // Default to mainnet (false)
    } catch (e) {
      console.error('Error getting saved network mode:', e);
      return false; // Default to mainnet
    }
  };

  const [selectedChain, setSelectedChain] = useState('TON');
  const [isTestnet, setIsTestnet] = useState(getSavedNetworkMode);
  const [provider, setProvider] = useState(null);
  const [chainFeatures, setChainFeatures] = useState({});
  const [networkInfo, setNetworkInfo] = useState(null);
  const [error, setError] = useState(null);
  const [networkHealth, setNetworkHealth] = useState({});
  const [isInitializing, setIsInitializing] = useState(true);

  // Save isTestnet state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('isTestnet', isTestnet.toString());
    } catch (e) {
      console.error('Error saving network mode:', e);
    }
  }, [isTestnet]);

  // Initialize provider and network info when chain or network type changes
  useEffect(() => {
    const initializeChain = async () => {
      try {
        setIsInitializing(true);

        // Load selected chain from localStorage if available
        try {
          const savedChain = localStorage.getItem('selectedChain');
          if (savedChain && SUPPORTED_CHAINS.includes(savedChain)) {
            setSelectedChain(savedChain);
          }
        } catch (e) {
          console.error('Error loading saved chain:', e);
        }

        // Validate chain configuration
        const configValidation = validateChainConfig(selectedChain);
        if (!configValidation.isValid) {
          throw new Error(`Invalid chain configuration: ${configValidation.error || configValidation.missingFields?.join(', ')}`);
        }

        // Check network health
        const health = await checkNetworkHealth(selectedChain, isTestnet);
        setNetworkHealth(health);

        if (!health.isHealthy) {
          // Attempt to reconnect if network is unhealthy
          const reconnected = await reconnectProvider(selectedChain, isTestnet);
          if (!reconnected) {
            throw new Error('Failed to establish network connection');
          }
        }

        // Get chain features
        const features = getChainFeatures(selectedChain);
        setChainFeatures(features);

        // Get network information
        const network = getChainNetwork(selectedChain, isTestnet);
        setNetworkInfo(network);

        // Initialize provider
        const newProvider = await getProvider(selectedChain, isTestnet);
        setProvider(newProvider);
        setError(null);
      } catch (err) {
        console.error('Chain initialization error:', err);
        const handled = await handleChainError(err, selectedChain, isTestnet);
        if (!handled || handled.error) {
          setError(err.message);
        }
      } finally {
        setIsInitializing(false);
      }
    };

    initializeChain();

    // Set up network health monitoring
    const healthCheckInterval = setInterval(async () => {
      try {
        const health = await checkNetworkHealth(selectedChain, isTestnet);
        setNetworkHealth(health);

        if (!health.isHealthy) {
          // Attempt recovery if network becomes unhealthy
          await reconnectProvider(selectedChain, isTestnet);
        }
      } catch (err) {
        console.error('Network health check failed:', err);
      }
    }, 30000); // Check every 30 seconds

    return () => {
      clearInterval(healthCheckInterval);
      clearProviderCache(selectedChain, isTestnet);
    };
  }, [selectedChain, isTestnet]);

  const switchChain = async (chainId) => {
    try {
      if (!SUPPORTED_CHAINS.includes(chainId)) {
        throw new Error(`Unsupported chain: ${chainId}`);
      }

      if (chainId === selectedChain) {
        return;
      }

      // Clear provider cache before switching
      clearProviderCache(selectedChain, isTestnet);
      
      setSelectedChain(chainId);
      localStorage.setItem('selectedChain', chainId);
      setError(null);

      // Dispatch custom event for chain change
      window.dispatchEvent(new CustomEvent(NETWORK_CHANGE_EVENT, { 
        detail: { 
          chain: chainId, 
          isTestnet: isTestnet,
          type: 'chain'
        } 
      }));
      
    } catch (err) {
      console.error('Chain switch error:', err);
      const handled = await handleChainError(err, chainId, isTestnet);
      if (!handled || handled.error) {
        setError(err.message);
        throw err;
      }
    }
  };

  const toggleNetwork = async () => {
    try {
      const newIsTestnet = !isTestnet;
      
      // Clear provider cache before switching network
      clearProviderCache(selectedChain, isTestnet);
      
      // Update the local state
      setIsTestnet(newIsTestnet);
      
      // Save to localStorage immediately
      try {
        localStorage.setItem('isTestnet', newIsTestnet.toString());
      } catch (e) {
        console.error('Error saving network mode:', e);
      }
      
      // Clear any errors
      setError(null);

      // Dispatch custom event for network type change
      window.dispatchEvent(new CustomEvent(NETWORK_CHANGE_EVENT, { 
        detail: { 
          chain: selectedChain, 
          isTestnet: newIsTestnet,
          type: 'network'
        } 
      }));
      
      // Create a loading overlay to show before reload
      const overlay = document.createElement('div');
      overlay.style.position = 'fixed';
      overlay.style.top = '0';
      overlay.style.left = '0';
      overlay.style.width = '100%';
      overlay.style.height = '100%';
      overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
      overlay.style.display = 'flex';
      overlay.style.justifyContent = 'center';
      overlay.style.alignItems = 'center';
      overlay.style.zIndex = '9999';
      overlay.style.transition = 'opacity 0.3s';
      overlay.style.backdropFilter = 'blur(5px)';
      
      const message = document.createElement('div');
      message.innerText = `Switching to ${newIsTestnet ? 'Testnet' : 'Mainnet'}...`;
      message.style.color = 'white';
      message.style.fontSize = '18px';
      message.style.fontWeight = 'bold';
      message.style.padding = '20px';
      message.style.borderRadius = '10px';
      message.style.background = 'rgba(0, 136, 204, 0.2)';
      
      overlay.appendChild(message);
      document.body.appendChild(overlay);
      
      // Force-reload the page after a short delay to ensure localStorage is updated
      // Use a more reliable approach to ensure the reload happens
      setTimeout(() => {
        try {
          // Force a full refresh (not from cache)
          window.location.href = window.location.href.split('#')[0] + 
            (window.location.href.includes('?') ? '&' : '?') + 
            'network=' + (newIsTestnet ? 'testnet' : 'mainnet') + 
            '&t=' + new Date().getTime();
        } catch (e) {
          // Fallback to regular reload
          window.location.reload(true);
        }
      }, 500);
      
    } catch (err) {
      console.error('Network toggle error:', err);
      const handled = await handleChainError(err, selectedChain, !isTestnet);
      if (!handled || handled.error) {
        setError(err.message);
        throw err;
      }
    }
  };

  const value = {
    chains: CHAIN_CONFIG,
    supportedChains: SUPPORTED_CHAINS,
    selectedChain,
    switchChain,
    isTestnet,
    toggleNetwork,
    provider,
    chainFeatures,
    networkInfo,
    networkHealth,
    error,
    isInitializing
  };

  return (
    <ChainContext.Provider value={value}>
      {children}
    </ChainContext.Provider>
  );
}; 